import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox'
import {
  formatSourceGroupSupplierInstallationLabel,
  type SourceGroupSupplierInstallationOptionDto,
} from '@/features/installations/api/source-group-model'
import { cn } from '@/shared/lib/utils'

type InstallationOption = {
  value: string
  label: string
}

type SourceGroupSupplierInstallationSelectProps = {
  options: SourceGroupSupplierInstallationOptionDto[]
  value: string
  onValueChange: (supplierInstallationId: string) => void
  placeholder: string
  disabled?: boolean
  id?: string
  triggerClassName?: string
  'aria-invalid'?: boolean
}

function matchesInstallationQuery(option: InstallationOption, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) {
    return true
  }
  return option.label.toLowerCase().includes(trimmed)
}

export function SourceGroupSupplierInstallationSelect({
  options,
  value,
  onValueChange,
  placeholder,
  disabled,
  id,
  triggerClassName,
  'aria-invalid': ariaInvalid,
}: SourceGroupSupplierInstallationSelectProps) {
  const { i18n, t } = useTranslation('installations')

  const comboboxOptions = useMemo(
    (): InstallationOption[] =>
      options.map((option) => ({
        value: option.id,
        label: formatSourceGroupSupplierInstallationLabel(option),
      })),
    [options]
  )

  const selectValue = value.trim() || undefined

  const selectedOption = useMemo(
    () => comboboxOptions.find((option) => option.value === selectValue) ?? null,
    [comboboxOptions, selectValue]
  )

  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.label)
    } else {
      setInputValue('')
    }
  }, [selectedOption])

  const handleOpenChangeComplete = useCallback(
    (open: boolean) => {
      if (!open && selectedOption) {
        setInputValue(selectedOption.label)
      }
    },
    [selectedOption]
  )

  return (
    <Combobox
      items={comboboxOptions}
      value={selectedOption}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      onOpenChangeComplete={handleOpenChangeComplete}
      disabled={disabled}
      locale={i18n.language}
      autoHighlight
      itemToStringLabel={(option) => option.label}
      isItemEqualToValue={(optionA, optionB) => optionA.value === optionB.value}
      filter={(item, query) => matchesInstallationQuery(item as InstallationOption, query)}
      onValueChange={(option) => {
        if (!option) {
          return
        }
        setInputValue(option.label)
        onValueChange(option.value)
      }}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        className={cn('h-9 w-full min-w-0', triggerClassName)}
      />
      <ComboboxContent className="max-h-[min(24rem,var(--available-height))]">
        <ComboboxEmpty>
          {t('configuration.sourceGroups.noSupplierInstallations')}
        </ComboboxEmpty>
        <ComboboxList>
          {(option: InstallationOption) => (
            <ComboboxItem key={option.value} value={option}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
